import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import * as SettingsState from '../../common/state/SettingsState.ts';
import * as AuthenticationState from '../state/AuthenticationState.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import {
    Navigate,
    Outlet,
    useLocation,
    useMatches,
    useParams,
} from 'react-router';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { css } from '@emotion/css';
import { Navbar } from '../../layout/components/Navbar.tsx';
import * as MasterDataZonesState from '../../common/state/MasterDataZonesState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import * as TariffState from '../../tariffs/state/TariffState.ts';
import {
    isRouteNameMatch,
    useIsMobileRoute,
} from '../../layout/components/BreadCrumb.tsx';
import { isRouteAllowed } from './OperatorRoutes.tsx';
import { ReadOnlyExportContextProvider } from './ReadOnlyExportContext.tsx';
import { DefaultServerHeadersContext } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { setOperatorExternalId } from '../../AsyncRequest.ts';
import { OperatorLoginData } from '../../common/state/OperatorLoginData.ts';
import { Spinner } from '../../ui/loaders/Spinner.tsx';
import { OPERATOR_EXTERNAL_ID_HEADER } from '../../utils/Utils.ts';
import { NoAccessDialog } from '../../operator-logins/components/NoAccessDialog.tsx';
import { useOperatorExternalIdFallback } from './OperatorExternalIdFallbackContext.tsx';
import { logger } from '@sentry/react';

const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type OperatorContextState = Readonly<{
    settings: SettingsState.State;
    authentication: AuthenticationState.AuthStatus;
    operatorData: OperatorDataState.OperatorData;
    currentLogin: OperatorLoginData;
    operatorExternalId: string;
}>;
export const OperatorContext = createContext<OperatorContextState>(
    {} as OperatorContextState, // This is technically wrong but we ensure it's set before we render more stuff.
);

export function useOperatorContext() {
    return useContext(OperatorContext);
}

export function BaseData({
    settings,
    authentication,
}: {
    settings: SettingsState.State;
    authentication: AuthenticationState.AuthStatus;
}) {
    const urlParams = useParams();
    const { setOperatorExternalIdFallback } = useOperatorExternalIdFallback();
    const operatorExternalId =
        urlParams.operatorExternalId != null &&
        UUID_REGEX.test(urlParams.operatorExternalId)
            ? urlParams.operatorExternalId
            : null;

    useEffect(() => {
        if (operatorExternalId != null) {
            setOperatorExternalIdFallback(operatorExternalId);
        }
    }, [operatorExternalId, setOperatorExternalIdFallback]);

    if (operatorExternalId == null) {
        return <RedirectToOperatorSelector />;
    }

    setOperatorExternalId(operatorExternalId);

    return (
        <OperatorContextProvider
            settings={settings}
            authentication={authentication}
            operatorExternalId={operatorExternalId}
        />
    );
}

function RedirectToOperatorSelector() {
    const location = useLocation();
    const isMobile = useIsMobileRoute();
    const { operatorExternalIdFallback } = useOperatorExternalIdFallback();

    if (operatorExternalIdFallback == null) {
        return <Navigate to={`${isMobile ? '/mobile' : ''}/select-operator`} />;
    }
    logger.error('OperatorExternalId fallback required', {
        route: location.pathname,
    });
    return (
        <Navigate
            to={{
                ...location,
                pathname: `/${operatorExternalIdFallback}${location.pathname}`,
            }}
            replace
        />
    );
}

function OperatorContextProvider({
    settings,
    authentication,
    operatorExternalId,
}: {
    settings: SettingsState.State;
    authentication: AuthenticationState.AuthStatus;
    operatorExternalId: string;
}) {
    const isMobile = useIsMobileRoute();
    const defaultServerHeaders = useMemo(
        () => ({
            [OPERATOR_EXTERNAL_ID_HEADER]: operatorExternalId,
        }),
        [operatorExternalId],
    );

    const matchedRouteNames = useMatches().filter(isRouteNameMatch);

    const { storeState } = useStore(s => ({
        operatorData: OperatorDataState.get(s),
        currentLogin: CurrentOperatorLoginState.get(s),
    }));
    const operatorDataState = storeState.operatorData;
    const currentLoginState = storeState.currentLogin;
    const operatorData = operatorDataState.data;
    const currentLogin = currentLoginState.data;
    const operatorContextValue = useMemo(() => {
        if (!operatorData || !currentLogin || operatorExternalId == null) {
            return null;
        }

        return {
            settings,
            authentication,
            operatorData,
            currentLogin,
            operatorExternalId,
        };
    }, [
        settings,
        authentication,
        operatorData,
        currentLogin,
        operatorExternalId,
    ]);

    if (operatorDataState.pending || currentLoginState.pending) {
        return <Spinner />;
    }

    if (
        operatorDataState.statusCode.forbidden ||
        currentLoginState.statusCode.forbidden
    ) {
        return (
            <NoAccessDialog
                open
                onClose={() => {
                    // reload app to reset all stores
                    window.location.replace(
                        `${isMobile ? '/mobile' : ''}/select-operator`,
                    );
                }}
            />
        );
    }

    if (!operatorData || !currentLogin) {
        return (
            <Dialog open fullWidth>
                <DialogTitle
                    sx={theme => ({
                        background: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                    })}
                >
                    <Localized de="Fehler" fr="Erreur" it="Errore" en="Error" />
                </DialogTitle>
                <DialogContent>
                    <p>
                        <Localized
                            de="Ein unerwarteter Fehler ist aufgetreten."
                            fr="Une erreur inattendue s'est produite."
                            it="Si è verificato un errore imprevisto."
                            en="An unexpected error has occurred."
                        />
                    </p>
                </DialogContent>
            </Dialog>
        );
    }

    const currentRouteName =
        matchedRouteNames[matchedRouteNames.length - 1].handle.routeName;

    if (!isRouteAllowed(operatorData, currentLogin, currentRouteName)) {
        return <div>not allowed</div>;
    }

    return (
        <OperatorContext.Provider value={operatorContextValue!}>
            <DefaultServerHeadersContext.Provider value={defaultServerHeaders}>
                <ReadOnlyExportContextProvider />
            </DefaultServerHeadersContext.Provider>
        </OperatorContext.Provider>
    );
}

export function BaseLayoutAndData() {
    const { storeState } = useStore(s => ({
        // These need to be refetched if the login changes
        zones: MasterDataZonesState.get(s),
        permitTypes: PermitTypeState.get(s),
        tariffs: TariffState.List.get(s),
    }));
    return (
        <div
            className={css({
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                minWidth: 1024,
                overflow: 'hidden',
            })}
        >
            <Outlet />
            <Navbar
                zones={storeState.zones}
                permitTypes={storeState.permitTypes}
                tariffs={storeState.tariffs}
            />
        </div>
    );
}
